import React, { useEffect, useRef, useState, useImperativeHandle } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Menu,
  MenuItem,
} from "@material-ui/core";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import ImageIcon from "@material-ui/icons/Image";
import Webcam from "react-webcam";
import useCamera from "../../../../hooks/useCamera";
import { useStore } from "../../../../hooks/useStore";
import NotCamera from "../../../Scan/NotCamera/NotCamera";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { dataURLtoFile } from "../../../../utils/imageUtil";

const useStyles = makeStyles(() => ({
  preview: {
    width: "200px",
    height: "200px",
    borderRadius: "14px",
    border: "2px dashed #D1D5DC",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    boxSizing: "border-box",
    color: "#99A1AF",
    cursor: "pointer",
    opacity: 1,
    background: "#F3F4F6",
    overflow: "hidden",
  },
  imageContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    overflow: "hidden",
    borderRadius: "14px",
    position: "relative",
  },
  previewImage: {
    width: "100% !important",
    height: "auto !important",
    objectFit: "cover !important" as any,
    objectPosition: "center top !important" as any,
    display: "block !important",
    minHeight: "100%",
  },
  icon: {
    fontSize: "40px",
    width: "40px",
    height: "40px",
    opacity: 1,
  },
  label: {
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "24px",
    color: "#6A7282",
  },
  labelContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  hintContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    maxWidth: "80%",
    textAlign: "center",
  },
  generalContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "20px",
  },
  cameraDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    minWidth: "520px",
  },
  videoWrapper: {
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#000",
    minHeight: "480px",
  },
  video: {
    width: "100%",
    height: "480px",
    display: "block",
    objectFit: "cover",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  clearButton: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.96)",
    color: "#D92D20",
    border: "1px solid rgba(217, 45, 32, 0.35)",
    boxShadow: "0 2px 8px rgba(217, 45, 32, 0.25)",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: "#B42318",
      boxShadow: "0 3px 10px rgba(180, 35, 24, 0.32)",
    },
  },
  dragActive: {
    borderColor: "#3b82f6 !important",
    backgroundColor: "rgba(59, 130, 246, 0.08) !important",
    borderStyle: "solid !important",
  },
}));

export type PicturePreviewHandle = {
  openCamera: () => void;
};

type PicturePreviewProps = {
  imageUrl?: string;
  onUpload?: (file: File | null, dataUrl?: string) => void;
  onClick?: () => void;
  label?: string;
  previewHeight?: number | string;
  allowImageViewer?: boolean;
  onImageViewerOpen?: () => void;
  allowClear?: boolean;
  disabled?: boolean;
};

const PicturePreview = React.forwardRef<PicturePreviewHandle, PicturePreviewProps>(
  function PicturePreview(
    {
      imageUrl = "",
      onUpload,
      onClick,
      label = "Tomar foto",
      previewHeight,
      allowImageViewer = false,
      onImageViewerOpen,
      allowClear = false,
      disabled = false,
    },
    ref
  ) {
    const classes = useStyles();
    const [previewUrl, setPreviewUrl] = useState<string>(imageUrl);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const webcamRef = useRef<Webcam>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { getListOfCameras } = useCamera();
    const { cameraStore } = useStore();

    useImperativeHandle(ref, () => ({
      openCamera: () => {
        if (!disabled) {
          setIsCameraOpen(true);
        }
      },
    }));

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) {
        if (hasImage && allowImageViewer) {
          onImageViewerOpen?.();
        }
        return;
      }
      onClick?.();
      if (hasImage && allowImageViewer) {
        onImageViewerOpen?.();
        return;
      }
      setMenuAnchorEl(event.currentTarget);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragActive(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    };

    const validateFile = (file: File): boolean => {
      const isAcceptedType =
        file.type === "image/png" ||
        file.type === "image/jpeg" ||
        file.name.toLowerCase().endsWith(".png") ||
        file.name.toLowerCase().endsWith(".jpg") ||
        file.name.toLowerCase().endsWith(".jpeg");

      const isAcceptedSize = file.size <= 10 * 1024 * 1024; // 10MB

      if (!isAcceptedType) {
        alert("Solo se permiten imágenes en formato PNG o JPEG/JPG.");
        return false;
      }

      if (!isAcceptedSize) {
        alert("El tamaño máximo permitido para la imagen es de 10MB.");
        return false;
      }

      return true;
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        if (!validateFile(file)) return;
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          setPreviewUrl(dataUrl);
          onUpload?.(file, dataUrl);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        if (!validateFile(file)) {
          e.target.value = "";
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          setPreviewUrl(dataUrl);
          onUpload?.(file, dataUrl);
        };
        reader.readAsDataURL(file);
      }
      e.target.value = "";
    };

    const stopWebcamTracks = () => {
      if (webcamRef.current) {
        if (webcamRef.current.stream) {
          webcamRef.current.stream.getTracks().forEach((track) => track.stop());
        }
        if (webcamRef.current.video?.srcObject) {
          try {
            const stream = webcamRef.current.video.srcObject as MediaStream;
            stream.getTracks().forEach((track) => track.stop());
          } catch (e) {
            console.error("Error stopping tracks on webcamRef:", e);
          }
          webcamRef.current.video.srcObject = null;
        }
      }
    };

    useEffect(() => {
      return () => {
        stopWebcamTracks();
      };
    }, []);

    const closeCameraDialog = () => {
      stopWebcamTracks();
      setIsCameraOpen(false);
    };

    const capture = () => {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) {
        return;
      }

      setPreviewUrl(imageSrc);
      onUpload?.(dataURLtoFile(imageSrc, `visitor-photo-${Date.now()}.jpg`), imageSrc);
      closeCameraDialog();
    };

    const clearImage = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setPreviewUrl("");
      onUpload?.(null);
    };

    useEffect(() => {
      setPreviewUrl(imageUrl);
    }, [imageUrl]);

    useEffect(() => {
      if (isCameraOpen) {
        getListOfCameras();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCameraOpen]);

    const hasImage = Boolean(previewUrl);

    return (
      <div className={classes.generalContainer}>
        <div
          onClick={handleClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleClick(event as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`${classes.preview} ${isDragActive ? classes.dragActive : ""}`}
          role="button"
          tabIndex={0}
          style={previewHeight ? { height: previewHeight, width: "100%" } : undefined}
        >
          {hasImage ? (
            <div className={classes.imageContainer}>
              {allowClear && !disabled && (
                <IconButton
                  className={classes.clearButton}
                  size="small"
                  aria-label="eliminar imagen"
                  onClick={clearImage}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              <img
                src={previewUrl}
                alt="preview"
                className={classes.previewImage}
                style={{
                  objectFit: "contain",
                  objectPosition: "center top",
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          ) : (
            <div className={classes.labelContainer}>
              <CameraAltIcon className={classes.icon} />
              <span className={classes.label}>{label}</span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "center",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "center",
          }}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setIsCameraOpen(true);
            }}
          >
            <CameraAltIcon style={{ marginRight: 8 }} />
            Tomar foto
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              fileInputRef.current?.click();
            }}
          >
            <ImageIcon style={{ marginRight: 8 }} />
            Buscar archivo
          </MenuItem>
        </Menu>

        <Dialog open={isCameraOpen} onClose={closeCameraDialog} maxWidth="md" fullWidth>
          {isCameraOpen && (
            <>
              <DialogTitle>
                Tomar foto
                <IconButton
                  aria-label="close"
                  className={classes.closeButton}
                  onClick={closeCameraDialog}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                <div className={classes.cameraDialogContent}>
                  {cameraStore.empty ? (
                    <NotCamera />
                  ) : (
                    <div className={classes.videoWrapper}>
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{
                          deviceId: cameraStore.deviceId || undefined,
                        }}
                        className={classes.video}
                      />
                    </div>
                  )}
                </div>
              </DialogContent>

              <DialogActions>
                <Button onClick={closeCameraDialog}>Cancelar</Button>
                <Button
                  onClick={capture}
                  color="primary"
                  variant="contained"
                  disabled={cameraStore.empty}
                >
                  Capturar
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </div>
    );
  }
);

export default PicturePreview;
